import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { LucideIcon } from 'lucide-react'

/**
 * Aktions-Slot der mobilen Kopfleiste in `__root.tsx`.
 *
 * Unter `md` steht die Root-Kopfleiste ohnehin ueber jeder Seite. Eine Route, die
 * eine eigene Leiste nur fuer einen einzigen Umschalter braeuchte, meldet ihn
 * stattdessen hier an — sonst stehen zwei fast identische Leisten uebereinander
 * und fressen von den ~600 px nutzbarer Hoehe rund 89 px.
 */
export interface MobileHeaderAction {
  icon: LucideIcon
  /** Beschriftung fuer Screenreader und Tooltip */
  label: string
  onClick: () => void
}

interface MobileHeaderActionContextValue {
  action: MobileHeaderAction | null
  setAction: React.Dispatch<React.SetStateAction<MobileHeaderAction | null>>
}

const MobileHeaderActionContext = createContext<MobileHeaderActionContextValue | null>(null)

export function MobileHeaderActionProvider({ children }: { children: React.ReactNode }) {
  const [action, setAction] = useState<MobileHeaderAction | null>(null)
  const value = useMemo(() => ({ action, setAction }), [action])

  return (
    <MobileHeaderActionContext.Provider value={value}>
      {children}
    </MobileHeaderActionContext.Provider>
  )
}

/** Liest den angemeldeten Eintrag — nur fuer die Kopfleiste selbst gedacht. */
export function useMobileHeaderAction() {
  return useContext(MobileHeaderActionContext)?.action ?? null
}

/**
 * Meldet eine Aktion fuer die Dauer des Mountings an.
 *
 * `onClick` darf bei jedem Render eine neue Funktion sein: der Handler wandert in
 * eine Ref, angemeldet wird ein stabiler Wrapper. Sonst wuerde jede Registrierung
 * ein Rerender der Shell ausloesen, das die naechste Registrierung nach sich zieht.
 */
export function useRegisterMobileHeaderAction(
  icon: LucideIcon,
  label: string,
  onClick: () => void,
) {
  const context = useContext(MobileHeaderActionContext)
  const setAction = context?.setAction
  const onClickRef = useRef(onClick)
  onClickRef.current = onClick

  useEffect(() => {
    if (!setAction) return

    const entry: MobileHeaderAction = { icon, label, onClick: () => onClickRef.current() }
    setAction(entry)
    // Nur den eigenen Eintrag zuruecknehmen, nie den einer nachfolgenden Route
    return () => setAction((current) => (current === entry ? null : current))
  }, [icon, label, setAction])
}
